import { z } from 'zod';
import { ANOMALY_VERDICTS_MIRROR, DETECTION_OUTCOMES_MIRROR } from './anomaly-families.js';
import { paginated, paginationQuerySchema } from './common.js';
/**
 * What the detector concluded, for a reader — the shadow-mode surface.
 *
 * ⚠️ **These are CANDIDATES, not incidents, and the distinction is the whole
 * screen.** Nothing here has been published: no event was raised, no incident
 * opened, no recommendation generated, nobody notified. Presenting them as a
 * queue would make the platform look as though it had decided something it has
 * deliberately not decided, and would put work in front of somebody on evidence
 * that has never been calibrated against a real feed.
 *
 * ⚠️ **The OUTCOME MIX is the headline, not the findings list.** On any real
 * tenant most candidates are `NORMAL`, `NO_BASELINE` or gated — and a screen
 * that showed only the publishable ones would report a detector finding things
 * while hiding that it refused to judge four fifths of what it looked at. The
 * refusals are the honest part, and they are what shadow mode is read for.
 */
export const anomalyObservationSchema = z.object({
    vehicleId: z.string().uuid(),
    vehicleName: z.string(),
    vehicleRegistration: z.string().nullable(),
    /**
     * Did the vehicle resolve inside THIS branch?
     *
     * ⚠️ **A flag, so a screen never string-matches the fallback sentence to
     * decide whether to render a link.** False means the vehicle has moved to
     * another branch — `Recommendation.vehicleId` and `OperationalIncident
     * .vehicleId` are joins by id with NO tenant in the key, which is why the
     * vehicle-transfer design calls them out. `/vehicles/:id` is tenant-scoped, so
     * linking one of these would 404 "Vehicle not found" and read as DELETED
     * rather than as somewhere else.
     */
    vehicleKnown: z.boolean(),
    /** `YYYY-MM-DD`, UTC — the day observed, never the day computed. */
    day: z.string(),
    featureId: z.string(),
    /**
     * What a PERSON calls the measurement — `Idling`, `Distance travelled`.
     *
     * ⚠️ **The screen showed the FAMILY and the reader could not tell what had
     * been measured.** `IDLE`, `ACTIVITY` and `CONNECTIVITY` are internal grouping
     * names; a row reading *"ACTIVITY · 800 against a usual 505"* names neither
     * the quantity nor its unit, so `800` could be minutes, kilometres or
     * messages. Read from `ANOMALY_FEATURES`, never restated.
     */
    label: z.string(),
    /**
     * The measurement's canonical unit — `km`, `minutes`, `count`.
     *
     * ⚠️ **Without it the number is unreadable, and this is the fault this
     * platform already records for `durationMs` against `durationSeconds`:** they
     * share a label and differ by a factor of a thousand, and only the unit tells
     * them apart. `320` reads as small; `5 h 20 m of idling` reads as a problem.
     */
    unit: z.string(),
    /** The anomaly family this feature belongs to, or null if none reads it. */
    family: z.string().nullable(),
    /**
     * Which population this day was compared against — `WORKING` or
     * `NON_WORKING`.
     *
     * ⚠️ **Without it a per-vehicle working week is INVISIBLE MAGIC.** A reader
     * looking at a weekend-only lorry cannot tell why its Saturday is normal, and
     * a reader whose findings changed after somebody edited a vehicle has nothing
     * to connect the two. The engine has always stored it per row; nothing showed
     * it.
     *
     * ⚠️ It is the CONTEXT, not the calendar. A vehicle's own week decides which
     * days fall in which population; this says which one this day landed in, which
     * is the fact the comparison actually rests on.
     */
    context: z.string(),
    /**
     * True where this day was judged under a DIFFERENT working week from the one
     * in force for the vehicle now.
     *
     * ⚠️ **The observation is never rewritten, so without this the screen
     * contradicts the vehicle's own settings.** A calendar is not part of an
     * observation's identity, so a re-run keeps the verdict reached at the time —
     * correct as provenance, and it means a reader can otherwise see *"Sunday · vs
     * non-working days"* on a vehicle whose Sunday is now a working day, with
     * nothing explaining it. Worse: somebody who corrects a vehicle's week
     * precisely to fix bad findings watches them persist.
     *
     * ⚠️ **FALSE where it cannot be told**, never true. A vehicle that has moved
     * branch is not resolvable here, and claiming its history was judged under
     * another calendar would be inventing a fact — absence is not staleness.
     */
    judgedUnderPreviousWeek: z.boolean(),
    outcome: z.enum(DETECTION_OUTCOMES_MIRROR),
    /**
     * ⚠️ NULL where no comparison was made — NOT zero. Zero means "exactly
     * typical", which is the opposite of "could not say", and a table that
     * rendered them alike would report a refusal as a perfect result.
     */
    detectionScore: z.number().nullable(),
    direction: z.enum(['ABOVE', 'BELOW']).nullable(),
    observed: z.number(),
    baselineMedian: z.number().nullable(),
    baselineSampleCount: z.number().int().nullable(),
    reliability: z.number().nullable(),
    /** Why this candidate ended where it did, in one line. */
    reason: z.string(),
});
export const anomalyOutcomeCountSchema = z.object({
    outcome: z.enum(DETECTION_OUTCOMES_MIRROR),
    count: z.number().int(),
    /** What this outcome MEANS, so a reader need not learn the vocabulary. */
    meaning: z.string(),
    /** True for the four that are refusals rather than measurements. */
    suppressed: z.boolean(),
});
export const anomalyMetricsSchema = z.object({
    /**
     * ⚠️ NULL below the evidence floor, and the COUNTS stay. Two false positives
     * out of three is 67%, which is arithmetic rather than evidence — and a reader
     * shown that number will act on it. The same floor `judgePolicyVersion` uses,
     * imported rather than re-chosen.
     */
    precision: z.number().nullable(),
    falsePositiveRate: z.number().nullable(),
    /** Reviewed outcomes so far, and how many the rate needs. Always present. */
    outcomes: z.number().int(),
    needed: z.number().int(),
    /**
     * ⚠️ **The earliest signal of alert fatigue, to be watched more closely than
     * precision** (§18). A detector can be perfectly precise and still ignored,
     * and by the time precision moves the readers have already stopped looking.
     */
    reviewRate: z.number().nullable(),
    publishedAgainstBudget: z.number().nullable(),
    /**
     * ⚠️ A sudden RISE is a data problem wearing the costume of a quiet week:
     * everything suppressed looks exactly like a fleet with nothing wrong.
     */
    suppressionRate: z.number().nullable(),
    confirmedPer100VehiclesPerMonth: z.number().nullable(),
    /** Why a withheld figure is withheld, so the screen says it rather than blank. */
    withheldReason: z.string().nullable(),
    /** Items published and opened in the window — the review rate's two figures. */
    published: z.number().int(),
    opened: z.number().int(),
    /** What the budget allowed this branch over the rolling window. */
    budgetAllowance: z.number().int(),
    /**
     * ⚠️ Whether this tenant PUBLISHES at all. Every figure above is zero in
     * shadow mode and that is the mode working — without this the screen cannot
     * tell "nothing was published" from "publication is switched off", and only
     * one of those is worth investigating.
     */
    publishing: z.boolean(),
});
export const anomalyOverviewSchema = z.object({
    /**
     * ⚠️ Every declared outcome, including the ZEROES. Absent and zero draw
     * identically in a table and mean opposite things — and on this screen the
     * difference is "the detector never hit that case" against "it did and the
     * row is missing".
     */
    outcomes: z.array(anomalyOutcomeCountSchema),
    evaluated: z.number().int(),
    /** Withheld below the evidence floor; the counts are always present. */
    suppressionRate: z.number().nullable(),
    findings: z.array(anomalyObservationSchema),
    /** Findings beyond the page, so a capped list never reads as the whole. */
    omitted: z.number().int(),
    /**
     * Candidates on measurements NO shipped family reads.
     *
     * ⚠️ **Counted, never listed, and never silently dropped.** The deviation is
     * real, but no family reads the feature — so `anomalyEventsFrom` returns
     * nothing for it and it could never become an incident even once the switch
     * is thrown. Listing them under "findings" inflates the count with items
     * nobody can ever act on; dropping them silently would hide that the
     * catalogue has moved ahead of its families. Found by screenshotting the
     * built page: they were a third of the list.
     */
    notInAFamily: z.number().int(),
    windowDays: z.number().int(),
    /** The window actually read, as UTC days — what the figures above describe. */
    from: z.string(),
    to: z.string(),
    /**
     * The oldest day this tenant has ANY observation for, or null if none.
     *
     * ⚠️ **This is what stops a range into the past reading as "nothing was
     * unusual".** Observations exist only for days the detector has RUN; a window
     * starting before the first run is not a quiet period, it is a period nobody
     * looked at. The two are indistinguishable from an empty list, and only one of
     * them is a fact about the fleet.
     */
    earliestObserved: z.string().nullable(),
    /**
     * ⚠️ **Always false until the switch is thrown**, and stated rather than
     * implied. A screen that simply showed findings would let a reader assume
     * somebody had been told about them.
     */
    published: z.boolean(),
    /** Vehicles the detector actually looked at — the denominator. */
    vehiclesConsidered: z.number().int(),
    /**
     * The six shadow-mode gates of design §19.0b, computed once.
     *
     * ⚠️ **This is what shadow mode is FOR.** A stage that stores observations and
     * measures nothing is a stage nobody can exit — the gates are how "is this
     * ready for a customer" gets an answer, and until they were rendered the only
     * way to ask was SQL.
     */
    metrics: anomalyMetricsSchema,
});
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
export const anomalyQuerySchema = z
    .object({
    days: z.coerce.number().int().min(1).max(730).default(7),
    /**
     * An explicit range, UTC days inclusive — `from` and `to` together.
     *
     * ⚠️ **Both or neither, and it OVERRIDES `days`.** A range with one end
     * supplied is ambiguous: "from the 3rd" could mean to today or to the end of
     * retention, and guessing would silently answer a different question from
     * the one asked.
     */
    from: isoDay.optional(),
    to: isoDay.optional(),
    /** Publishable only by default: the rest are available and not the point. */
    outcome: z.enum(DETECTION_OUTCOMES_MIRROR).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
    .strict()
    .refine((q) => (q.from === undefined) === (q.to === undefined), {
    message: 'from and to must be given together',
})
    .refine((q) => q.from === undefined || q.to === undefined || q.from <= q.to, {
    message: 'from must not be after to',
});
/**
 * What each outcome is CALLED on a screen.
 *
 * ⚠️ **DISPLAY only — the enum is untouched**, exactly as the recommendation
 * statuses are. `GATED_RELIABILITY` is the API's vocabulary, the value a filter
 * sends, the word an audit row carries and what an integrator reads in the
 * published spec; a migration in service of a chip would be the wrong trade.
 *
 * ⚠️ **Reported from the built page: "difficult to understand what it means
 * for a non-tech user".** *Gated reliability* is engineering vocabulary — the
 * reader of this screen is a transport manager deciding whether a vehicle needs
 * chasing, and the word describes the mechanism rather than the situation. Every
 * label here says what the DETECTOR CONCLUDED, in the words somebody would use
 * out loud.
 *
 * An unrecognised outcome falls through to its enum, never to a placeholder: a
 * state this table has not caught up with is still real, and ugly beats
 * "Unknown" on a row that has one.
 */
export const OUTCOME_LABELS = {
    PUBLISHABLE: 'Worth a look',
    NORMAL: 'Normal for this vehicle',
    UNDECIDABLE: 'Nothing to compare against',
    NO_BASELINE: 'Too new to compare',
    GATED_RELIABILITY: 'History not dependable enough',
    DEGRADED_INPUT: 'Too little of the day reported',
    PLATFORM_HEALTH: 'A gap on our side',
};
export function outcomeLabel(outcome) {
    return OUTCOME_LABELS[outcome] ?? outcome.replace(/_/g, ' ').toLowerCase();
}
/**
 * ⚠️ One sentence per outcome, so the screen never shows a reader a bare enum.
 * `NO_BASELINE` and `GATED_RELIABILITY` are the pair that matters: the first
 * resolves itself as history accrues and the second does not, and a reader told
 * only "suppressed" would wait for one that waiting never fixes.
 *
 * ⚠️ **Written for somebody who runs a fleet, not somebody who built the
 * detector.** The first version said *"History exists and is not trustworthy
 * enough to judge against. Unlike a cold start, waiting does not fix this"* —
 * every word of which is accurate and three of which ("history" as a technical
 * object, "judge against", "cold start") are ours rather than theirs. Each
 * sentence now says what happened, what it means for the vehicle, and whether
 * there is anything to do.
 *
 * ⚠️ **None of them NAMES A CAUSE the platform cannot see.** `reliability` is
 * a product of five factors — how much history, how much it varied, how recent
 * it is, how completely the vehicle reported, and whether the baseline had to be
 * borrowed from similar vehicles — and which one pulled it under the floor is
 * not recorded. Saying "the sensor is stuck" would send somebody to a workshop
 * on a guess; saying it "usually points at the device or at patchy reporting"
 * names the two families and stops.
 */
export const OUTCOME_MEANINGS = {
    PUBLISHABLE: 'Clearly different from what this vehicle normally does, with enough history behind it to be worth someone looking.',
    NORMAL: 'In line with what this vehicle normally does.',
    UNDECIDABLE: 'This vehicle sent exactly the same number every day, so there is no normal range to compare this day against. That usually means a reading is stuck rather than a vehicle that never varies.',
    NO_BASELINE: 'We have not watched this vehicle for long enough yet. This sorts itself out — it takes about three weeks of readings.',
    GATED_RELIABILITY: 'We do have readings for this vehicle, but not enough dependable ones to say what normal looks like — so we cannot tell whether this day stood out. Unlike a brand-new vehicle, more time on its own will not fix it: it usually points at the device or at how patchily it reports.',
    DEGRADED_INPUT: 'The vehicle reported for too little of this day to compare it with anything.',
    PLATFORM_HEALTH: 'We know our own collection had a gap covering this day, so the missing readings are ours to explain rather than a sign of anything wrong with the vehicle.',
};
/**
 * ⚠️ A READER, so no caller indexes the record directly. `OUTCOME_MEANINGS` is
 * keyed by `string`, so a bare lookup is `string | undefined` and every call
 * site would grow its own fallback — which is how two of them come to disagree
 * about what an unrecognised outcome says. An empty string, never a
 * placeholder: a sentence this table has not caught up with should be absent
 * rather than claim something.
 */
export function outcomeMeaning(outcome) {
    return OUTCOME_MEANINGS[outcome] ?? '';
}
export const SUPPRESSED_OUTCOMES = [
    'UNDECIDABLE',
    'NO_BASELINE',
    'GATED_RELIABILITY',
    'DEGRADED_INPUT',
    'PLATFORM_HEALTH',
];
/**
 * Recording whether the detector was right — design §18, WP-14.
 *
 * ⚠️ **The classification field IS the measurement instrument**, which is why
 * §18 makes it a release requirement rather than reporting added later. Without
 * it the pipeline produces observations nobody can read and the shadow-mode
 * gates have nothing to be measured on.
 */
export const anomalyReviewRequestSchema = z
    .object({
    /**
     * ⚠️ **`EXPECTED_BEHAVIOUR` and `FALSE_POSITIVE` are DISTINCT and must stay
     * so.** The first means the platform was right to notice and a person
     * decided it was fine; the second means it should not have looked at all.
     * Collapsing them hides the difference between a well-calibrated detector
     * and a badly calibrated one — which is the difference every threshold
     * change is argued from.
     */
    verdict: z.enum(ANOMALY_VERDICTS_MIRROR),
    /**
     * ⚠️ OPTIONAL, deliberately. A required note at the end of a job produces
     * fabricated answers, which are worse than none — the rule the outcome form
     * already follows. It is required for a DISMISSAL there because that is the
     * signal being gathered; here the verdict itself is the signal.
     */
    note: z.string().trim().max(2000).optional(),
})
    .strict();
export const anomalyReviewResponseSchema = z.object({
    incidentId: z.string().uuid(),
    verdict: z.enum(ANOMALY_VERDICTS_MIRROR),
});
/**
 * Switching a customer's anomaly detection from SHADOW to LIVE — WP-17.
 *
 * ⚠️ **PLATFORM-scoped, and that is the rollout plan rather than a preference.**
 * "The first enablement is a tenant somebody is in contact with, and the
 * measurement that matters is the review rate in the first fortnight." Shadow
 * mode exists precisely so a detector nobody has calibrated cannot start
 * creating work inside a customer — and a control the customer could reach
 * would let them switch it on before anyone had looked at their baselines,
 * which is the one thing the stage is for.
 *
 * ⚠️ **The MODE is not the ENTITLEMENT.** `ANOMALY_DETECTION` says the customer
 * bought it; the mode says whether findings reach anybody. Both are needed, and
 * conflating them would make "sold" mean "switched on".
 */
export const anomalyPublicationResponseSchema = z.object({
    tenantId: z.string().uuid(),
    tenantName: z.string(),
    mode: z.enum(['SHADOW', 'LIVE']),
    /**
     * Whether the organisation is entitled at all.
     *
     * ⚠️ **A mode without the entitlement does NOTHING**, so the screen has to be
     * able to say which absence a reader is looking at — the `samplesRead` rule.
     * "Nothing published because it is in shadow" and "nothing published because
     * they never bought it" draw identically and need different things done.
     */
    entitled: z.boolean(),
    /**
     * Findings already published under this organisation.
     *
     * ⚠️ **Shown because switching back to SHADOW does NOT remove them.** Once
     * incidents exist they are somebody's work queue; the escape hatch is to stop
     * creating them, never to delete them. A screen that offered the switch
     * without saying so would imply an undo that does not exist.
     */
    publishedIncidents: z.number().int(),
});
export const updateAnomalyPublicationSchema = z
    .object({ mode: z.enum(['SHADOW', 'LIVE']) })
    .strict();
/**
 * The findings as a LIST — paginated, sorted, searchable.
 *
 * ⚠️ **Its own endpoint, separate from the overview.** The overview answers
 * *what did the detector do* and its figures are expensive: an outcome mix over
 * the window, the six gates, the earliest observed day. The list answers *show
 * me the rows*, and a reader paging or re-sorting it would otherwise recompute
 * all of that on every click — and see the cards flicker while doing it.
 *
 * ⚠️ **The family filter had to move to the SERVER with paging.** The overview
 * fetched a capped list and dropped the rows no family reads client-side, which
 * was fine for a footnote and is wrong for a page: filtering after the cut
 * returns fewer than a page of rows, so page 2 of 5 would be short for reasons
 * nothing on screen explains.
 */
/**
 * What the findings list can be ordered by.
 *
 * ⚠️ **Two, and `vehicleName` and `label` are deliberately NOT among them.**
 * Neither is a column: a name is resolved from the vehicle table and a label
 * from the feature catalogue, so the database can only order by a uuid and a
 * feature id — which looks like sorting and is not. Sorting the fetched PAGE
 * instead is worse, because the header then draws an arrow claiming the whole
 * list is ordered while page two starts the alphabet again: a confident wrong
 * answer, and the silent-truncation fault the search on this endpoint already
 * refuses. Finding one vehicle is what the SEARCH is for, and it is total.
 */
export const ANOMALY_FINDING_SORTS = ['day', 'detectionScore'];
export const anomalyFindingSortSchema = z.enum(ANOMALY_FINDING_SORTS);
export const anomalyFindingListQuerySchema = paginationQuerySchema
    .extend({
    sort: anomalyFindingSortSchema.default('detectionScore'),
    days: z.coerce.number().int().min(1).max(730).default(7),
    from: isoDay.optional(),
    to: isoDay.optional(),
    outcome: z.enum(DETECTION_OUTCOMES_MIRROR).optional(),
    /** One anomaly family, e.g. `IDLE`. Uppercase, as the registry declares. */
    family: z.string().trim().min(1).optional(),
    /**
     * ⚠️ Matches the vehicle NAME and its REGISTRATION, and nothing else.
     * A plate is what is painted on the vehicle and written on the job sheet;
     * matching the reason sentence would match text a reader cannot scan for,
     * and matching the feature id would match a word they never see.
     */
    search: z.string().trim().min(1).optional(),
    /**
     * ⚠️ **Rows on measurements NO family reads are excluded by DEFAULT**, and
     * this is the switch that says so out loud. They could never become an
     * incident — `anomalyEventsFrom` returns nothing for them — so listing them
     * pads the page with items nobody can act on. Counted in `notInAFamily`
     * either way, because dropping them silently would hide that the catalogue
     * has moved ahead of its families.
     */
    /*
     * ⚠️ A STRING ENUM, never `z.coerce.boolean()` — risk R8. `?x=false`
     * arrives as the string "false", which is truthy, and coerces to TRUE: the
     * filter would return exactly the rows a reader was excluding. And not a
     * `.transform` either, because that makes it a `ZodEffects` which the
     * OpenAPI generator drops — the parameter would vanish from the published
     * spec while still working. The service compares the string; that costs one
     * comparison and keeps the spec honest.
     */
    includeUnreadable: z.enum(['true', 'false']).optional(),
})
    .strict()
    .refine((q) => (q.from === undefined) === (q.to === undefined), {
    message: 'from and to must be given together',
})
    .refine((q) => q.from === undefined || q.to === undefined || q.from <= q.to, {
    message: 'from must not be after to',
});
export const anomalyFindingListResponseSchema = paginated(anomalyObservationSchema).extend({
    /** The window actually read, so the page states it rather than the picker. */
    from: z.string(),
    to: z.string(),
    /**
     * Rows in this window on measurements no shipped family reads.
     *
     * ⚠️ Counted over the WHOLE window, never the page: a per-page figure would
     * change as somebody paged and mean nothing.
     */
    notInAFamily: z.number().int(),
    /** Families present in this window, so the filter offers only what is there. */
    families: z.array(z.string()),
});
//# sourceMappingURL=anomaly-observations.js.map